import { useLingui } from '@lingui/react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PresenceSource,
  type PresenceSourceType,
  type SlotChildPresence,
} from '@/types/coverage';

interface PresenceSourceBadgeProps {
  source: PresenceSourceType;
  samples?: number;
}

/** A small label naming where a forecast time comes from. */
export function PresenceSourceBadge({ source, samples }: PresenceSourceBadgeProps) {
  const { i18n } = useLingui();

  let label: string;
  let className: string;
  switch (source) {
    case PresenceSource.HISTORY:
      label =
        samples !== undefined
          ? i18n._({
              id: 'shifts.presence.source.historySamples',
              message: 'History ({samples} days)',
              values: { samples },
            })
          : i18n._({ id: 'shifts.presence.source.history', message: 'History' });
      className = 'bg-info-muted text-info-on-surface';
      break;
    case PresenceSource.EXPECTED:
      label = i18n._({ id: 'shifts.presence.source.expected', message: 'Expected' });
      className = 'bg-primary/10 text-foreground';
      break;
    case PresenceSource.BOOKING:
    default:
      label = i18n._({ id: 'shifts.presence.source.booking', message: 'Booking' });
      className = 'bg-muted text-muted-foreground';
      break;
  }

  return (
    <span
      data-source={source}
      className={cn('inline-flex shrink-0 rounded px-1 text-xs font-medium leading-tight', className)}
    >
      {label}
    </span>
  );
}

/** The reason a child is flagged, or undefined when it is not. */
export function useOutsideBookingReason() {
  const { i18n } = useLingui();
  return (child: SlotChildPresence): string | undefined => {
    if (!child.outside_booking) return undefined;
    if (expectedTimeOutsideBooking(child)) {
      return child.booked_from && child.booked_to
        ? i18n._({
            id: 'shifts.presence.expectedOutsideBooking',
            message: 'Expected time is outside the booking ({from}-{to})',
            values: { from: child.booked_from, to: child.booked_to },
          })
        : i18n._({
            id: 'shifts.presence.expectedOutsideBookingNoTimes',
            message: 'Expected time is outside the booking',
          });
    }
    return child.booked_from && child.booked_to
      ? i18n._({
          id: 'shifts.presence.outsideBooking',
          message: 'Check-in history is outside the booking ({from}-{to})',
          values: { from: child.booked_from, to: child.booked_to },
        })
      : i18n._({
          id: 'shifts.presence.outsideBookingNoTimes',
          message: 'Check-in history is outside the booking',
        });
  };
}

/** True when the time outside the booked window is an expected time (#1583). */
function expectedTimeOutsideBooking(child: SlotChildPresence): boolean {
  const arrivalOutside =
    String(child.arrival_source) === PresenceSource.EXPECTED &&
    !!child.booked_from &&
    child.arrival < child.booked_from;
  const departureOutside =
    String(child.departure_source) === PresenceSource.EXPECTED &&
    !!child.booked_to &&
    child.departure > child.booked_to;
  return arrivalOutside || departureOutside;
}

/**
 * The drift warning of a child, or undefined when neither expected time differs from the
 * check-in history by more than 30 minutes (#1583).
 */
export function useDriftReason() {
  const { i18n } = useLingui();
  const describe = (minutes: number, field: 'arrival' | 'departure'): string => {
    const amount = Math.abs(minutes);
    if (field === 'arrival') {
      return minutes < 0
        ? i18n._({
            id: 'shifts.presence.arrivalDriftEarlier',
            message: 'Expected arrival is {amount} min earlier than usual',
            values: { amount },
          })
        : i18n._({
            id: 'shifts.presence.arrivalDriftLater',
            message: 'Expected arrival is {amount} min later than usual',
            values: { amount },
          });
    }
    return minutes < 0
      ? i18n._({
          id: 'shifts.presence.departureDriftEarlier',
          message: 'Expected departure is {amount} min earlier than usual',
          values: { amount },
        })
      : i18n._({
          id: 'shifts.presence.departureDriftLater',
          message: 'Expected departure is {amount} min later than usual',
          values: { amount },
        });
  };
  return (child: SlotChildPresence): string | undefined => {
    const reasons: string[] = [];
    if (child.arrival_drift_minutes != null) reasons.push(describe(child.arrival_drift_minutes, 'arrival'));
    if (child.departure_drift_minutes != null) reasons.push(describe(child.departure_drift_minutes, 'departure'));
    return reasons.length > 0 ? reasons.join('. ') : undefined;
  };
}

interface OutsideBookingIconProps {
  reason: string;
}

/** A warning icon whose accessible name is the reason. Also used for the drift warning. */
export function OutsideBookingIcon({ reason }: OutsideBookingIconProps) {
  return (
    <AlertTriangle
      role="img"
      aria-label={reason}
      className="h-3.5 w-3.5 shrink-0 text-warning-on-surface"
    >
      <title>{reason}</title>
    </AlertTriangle>
  );
}
